import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, CalendarDays, MessageSquare, Check, X, RotateCcw, MessageCircle } from "lucide-react";
import { CONTACT } from "@/data/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authConfig = () => ({
  withCredentials: true,
  headers: { Authorization: `Bearer ${localStorage.getItem("zuri_admin_token") || ""}` },
});

const statusColor = {
  pending: "text-yellow-400 border-yellow-400/40",
  confirmed: "text-green-400 border-green-400/40",
  declined: "text-red-400 border-red-400/40",
};

export default function AdminDashboard() {
  const [reservations, setReservations] = useState(null);
  const [messages, setMessages] = useState(null);
  const [tab, setTab] = useState("reservations");
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const [r, m] = await Promise.all([
        axios.get(`${API}/reservations`, authConfig()),
        axios.get(`${API}/contact`, authConfig()),
      ]);
      setReservations(r.data);
      setMessages(m.data);
    } catch (err) {
      if (err.response?.status === 401) {
        navigate("/admin/login");
      } else {
        toast.error("Failed to load data");
      }
    }
  }, [navigate]);

  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/reservations/${id}`, { status }, authConfig());
      setReservations((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      toast.success(`Reservation ${status}`);
    } catch {
      toast.error("Update failed");
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`, {}, authConfig()); } catch { /* ignore */ }
    localStorage.removeItem("zuri_admin_token");
    navigate("/admin/login");
  };

  if (reservations === null) {
    return <div className="min-h-screen flex items-center justify-center text-white/50 pt-24" data-testid="admin-loading">Loading...</div>;
  }

  const pending = reservations.filter((r) => r.status === "pending").length;

  return (
    <div className="min-h-screen pt-28 pb-24 max-w-7xl mx-auto px-6 md:px-12" data-testid="admin-dashboard-page">
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
        <div>
          <p className="text-xs uppercase tracking-[0.35em] text-[#d4af37] mb-2">Staff Portal</p>
          <h1 className="font-serif-display text-4xl font-light text-white">Enquiry Dashboard</h1>
        </div>
        <button onClick={logout} className="btn-outline-gold px-5 py-2.5 text-[12px] tracking-[0.16em] uppercase inline-flex items-center gap-2 self-start sm:self-auto" data-testid="admin-logout-btn">
          <LogOut size={14} /> Sign Out
        </button>
      </div>

      <div className="grid grid-cols-3 gap-4 mb-10">
        {[["Reservations", reservations.length], ["Pending", pending], ["Messages", messages?.length || 0]].map(([label, num]) => (
          <div key={label} className="bg-[#121212] border hairline p-5">
            <p className="font-serif-display text-3xl gold-text">{num}</p>
            <p className="text-xs uppercase tracking-widest text-white/50 mt-1">{label}</p>
          </div>
        ))}
      </div>

      <div className="flex gap-3 mb-8">
        <button onClick={() => setTab("reservations")} className={`px-5 py-2.5 text-[12px] uppercase tracking-[0.16em] border inline-flex items-center gap-2 transition-colors duration-300 ${tab === "reservations" ? "bg-[#d4af37] text-black border-[#d4af37]" : "border-white/15 text-white/60 hover:text-white"}`} data-testid="admin-tab-reservations">
          <CalendarDays size={14} /> Reservations
        </button>
        <button onClick={() => setTab("messages")} className={`px-5 py-2.5 text-[12px] uppercase tracking-[0.16em] border inline-flex items-center gap-2 transition-colors duration-300 ${tab === "messages" ? "bg-[#d4af37] text-black border-[#d4af37]" : "border-white/15 text-white/60 hover:text-white"}`} data-testid="admin-tab-messages">
          <MessageSquare size={14} /> Contact Messages
        </button>
      </div>

      {tab === "reservations" && (
        <div className="space-y-4" data-testid="admin-reservations-list">
          {reservations.length === 0 && <p className="text-white/40">No reservation enquiries yet.</p>}
          {reservations.map((r) => (
            <div key={r.id} className="bg-[#121212] border hairline p-6 grid grid-cols-1 lg:grid-cols-12 gap-4 items-center" data-testid={`reservation-row-${r.id}`}>
              <div className="lg:col-span-3">
                <p className="text-white font-medium">{r.full_name}</p>
                <p className="text-xs text-white/50 mt-1">{r.email}</p>
                <p className="text-xs text-white/50">{r.phone}</p>
              </div>
              <div className="lg:col-span-3">
                <p className="text-sm text-[#d4af37]">{r.room_type}</p>
                <p className="text-xs text-white/50 mt-1">{r.check_in} → {r.check_out}</p>
                <p className="text-xs text-white/50">{r.adults} adult(s), {r.children} child(ren)</p>
              </div>
              <div className="lg:col-span-3">
                <p className="text-xs text-white/60 italic">{r.special_requests || "No special requests"}</p>
                <p className="text-[10px] text-white/30 mt-1">{new Date(r.created_at).toLocaleString()}</p>
              </div>
              <div className="lg:col-span-3 flex items-center gap-3 lg:justify-end">
                <a
                  href={`https://wa.me/${CONTACT.adminWhatsapp}?text=${encodeURIComponent(`Booking review — Zuri Sana Beach Hotel\nGuest: ${r.full_name}\nRoom: ${r.room_type}\nDates: ${r.check_in} to ${r.check_out}\nGuests: ${r.adults}A/${r.children}C\nPhone: ${r.phone}\nStatus: ${r.status}`)}`}
                  target="_blank" rel="noopener noreferrer" title="Send to WhatsApp"
                  className="h-9 w-9 border border-[#25D366]/50 text-[#25D366] flex items-center justify-center hover:bg-[#25D366]/10 transition-colors duration-300"
                  data-testid={`whatsapp-share-btn-${r.id}`}
                ><MessageCircle size={15} /></a>
                <span className={`text-[10px] uppercase tracking-widest border px-2.5 py-1 ${statusColor[r.status]}`} data-testid={`reservation-status-${r.id}`}>{r.status}</span>
                {r.status === "pending" ? (
                  <>
                    <button onClick={() => setStatus(r.id, "confirmed")} title="Confirm" className="h-9 w-9 border border-green-400/40 text-green-400 flex items-center justify-center hover:bg-green-400/10 transition-colors duration-300" data-testid={`confirm-btn-${r.id}`}><Check size={15} /></button>
                    <button onClick={() => setStatus(r.id, "declined")} title="Decline" className="h-9 w-9 border border-red-400/40 text-red-400 flex items-center justify-center hover:bg-red-400/10 transition-colors duration-300" data-testid={`decline-btn-${r.id}`}><X size={15} /></button>
                  </>
                ) : (
                  <button onClick={() => setStatus(r.id, "pending")} title="Reset to pending" className="h-9 w-9 border border-white/20 text-white/50 flex items-center justify-center hover:bg-white/10 transition-colors duration-300" data-testid={`reset-btn-${r.id}`}><RotateCcw size={14} /></button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "messages" && (
        <div className="space-y-4" data-testid="admin-messages-list">
          {messages.length === 0 && <p className="text-white/40">No contact messages yet.</p>}
          {messages.map((m) => (
            <div key={m.id} className="bg-[#121212] border hairline p-6" data-testid={`message-row-${m.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <p className="text-white font-medium">{m.name} <span className="text-white/40 text-sm">· {m.email}</span></p>
                <p className="text-[10px] text-white/30">{new Date(m.created_at).toLocaleString()}</p>
              </div>
              {m.subject && <p className="text-sm text-[#d4af37] mb-2">{m.subject}</p>}
              <p className="text-sm text-white/70 leading-relaxed">{m.message}</p>
              <a href={`mailto:${m.email}`} className="inline-block mt-4 text-xs uppercase tracking-[0.16em] text-[#d4af37] hover:underline" data-testid={`reply-link-${m.id}`}>Reply by email</a>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
